import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ServiceRequestModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    priority: '',
    description: '',
    project: '',
    attachments: []
  });

  const categoryOptions = [
    { value: 'tecnico', label: 'Suporte Técnico' },
    { value: 'manutencao', label: 'Manutenção' },
    { value: 'instalacao', label: 'Instalação' },
    { value: 'treinamento', label: 'Treinamento' },
    { value: 'consultoria', label: 'Consultoria' },
    { value: 'outro', label: 'Outro' }
  ];

  const priorityOptions = [
    { value: 'baixa', label: 'Baixa' },
    { value: 'media', label: 'Média' },
    { value: 'alta', label: 'Alta' },
    { value: 'critica', label: 'Crítica' }
  ];

  const projectOptions = [
    { value: 'consultflow-pro', label: 'ConsultFlow Pro' },
    { value: 'sistema-gestao', label: 'Sistema de Gestão Integrada' },
    { value: 'infraestrutura-ti', label: 'Infraestrutura de TI' },
    { value: 'seguranca-dados', label: 'Segurança de Dados' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (formData?.title && formData?.category && formData?.priority && formData?.description) {
      onSubmit(formData);
      setFormData({
        title: '',
        category: '',
        priority: '',
        description: '',
        project: '',
        attachments: []
      });
      onClose();
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e?.target?.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">
                Nova Solicitação de Serviço
              </h2>
              <p className="text-sm text-muted-foreground">
                Descreva sua necessidade e nossa equipe entrará em contato
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Título da Solicitação"
              placeholder="Descreva brevemente o problema ou necessidade"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              required
            />
            <Select
              label="Projeto Relacionado"
              placeholder="Selecione o projeto"
              options={projectOptions}
              value={formData?.project}
              onChange={(value) => handleInputChange('project', value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Categoria"
              placeholder="Selecione a categoria"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleInputChange('category', value)}
              required
            />
            <Select
              label="Prioridade"
              placeholder="Selecione a prioridade"
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => handleInputChange('priority', value)}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Descrição Detalhada *
            </label>
            <textarea
              className="w-full min-h-[120px] px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
              placeholder="Descreva detalhadamente o problema, erro ou necessidade. Inclua informações como quando ocorreu, frequência, impacto no negócio, etc."
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              required
            />
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Anexos (Opcional)
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-4">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Icon name="Upload" size={24} className="text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground text-center">
                  Clique para selecionar arquivos ou arraste aqui
                  <br />
                  <span className="text-xs">PDF, DOC, JPG, PNG até 10MB cada</span>
                </span>
              </label>
            </div>

            {/* Attachment List */}
            {formData?.attachments?.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData?.attachments?.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="File" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-card-foreground">{file?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file?.size / 1024 / 1024)?.toFixed(1)} MB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              * Campos obrigatórios
            </p>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                iconName="Send"
                iconPosition="right"
              >
                Enviar Solicitação
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceRequestModal;